import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthDialog } from '@/components/auth/auth-dialog';
import { useAuth } from '@/lib/auth/context';
import { supabase } from '@/lib/supabase/client';
import { ThemeToggle } from '@/components/theme/theme-toggle';

export function Header() {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const { user, signOut } = useAuth();
  const [fullName, setFullName] = useState<string>('');
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    // Temporarily hidden until fully developed
    // { path: '/about', label: 'About' },
    // { path: '/pricing', label: 'Pricing' },
  ];

  useEffect(() => {
    async function getProfile() {
      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setFullName(data.full_name);
        }
      }
    }

    getProfile();
  }, [user]);

  return (
    <>
      <header className="fixed left-0 right-0 top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 transition-colors duration-300 dark:border-dark-border dark:bg-dark-bg/95">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-10 w-10 text-primary-500" />
              <span className="bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-4xl md:text-5xl font-bold text-transparent dark:from-primary-400 dark:to-primary-500">
                StudySnap
              </span>
            </Link>
            <nav className="hidden md:block">
              <ul className="flex items-center gap-6">
                {navLinks.map(({ path, label }) => (
                  <li key={path}>
                    <Link
                      to={path}
                      className={`text-lg ${
                        location.pathname === path
                          ? 'text-primary-500 dark:text-primary-400'
                          : 'text-gray-600 hover:text-gray-900 dark:text-dark-muted dark:hover:text-dark-text'
                      }`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
                <li>
                  {user ? (
                    <Link
                      to="/newoutput"
                      className={`rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-3 text-lg font-medium text-white transition-all hover:from-primary-600 hover:to-primary-700 dark:from-primary-400 dark:to-primary-500 dark:hover:from-primary-500 dark:hover:to-primary-600 ${
                        location.pathname === '/newoutput'
                          ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-primary-400'
                          : ''
                      }`}
                    >
                      Try Now
                    </Link>
                  ) : (
                    <button
                      onClick={() => setIsAuthOpen(true)}
                      className="rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-3 text-lg font-medium text-white transition-all hover:from-primary-600 hover:to-primary-700 dark:from-primary-400 dark:to-primary-500 dark:hover:from-primary-500 dark:hover:to-primary-600"
                    >
                      Try Now
                    </button>
                  )}
                </li>
              </ul>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <ThemeToggle />
            {user ? (
              <div className="flex items-center gap-4">
                <span className="text-lg text-gray-700 dark:text-dark-text">
                  {fullName || user.email}
                </span>
                <Button
                  variant="outline"
                  onClick={signOut}
                  className="text-lg px-6 py-2 h-auto"
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setIsAuthOpen(true)}
                className="text-lg px-6 py-2 h-auto"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <AuthDialog
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
      />
    </>
  );
}